import { FeatureSwitchKeys } from './feature-switch-keys';

/**
 * Every page a staff user can open from the side menu, and the gates each one needs, for the Role Features
 * "Check access" panel.
 *
 * - `route` pages declare their gates on the route (data.canUse in app.routing.ts, applied by canUseGuard);
 *   page-gates.spec.ts keeps `feature` and `role` in step with it.
 * - `menu` pages only require a sign-in at the route; the side menu shows the link only to users holding
 *   `feature`, and the page reads the same feature. page-gates.spec.ts fails when a side-menu feature has no
 *   entry here.
 *
 * `switchKey` is the feature switch the page or tab also checks. A tab (Settings, Nudger) shares its page's
 * route, so it carries the page's gates plus its own switch.
 */
export interface PageGate {
  label: string;
  /** The route path, without a leading slash. */
  route: string;
  feature: string;
  role?: string;
  switchKey?: string;
  /** Where the gates are declared: on the route, or by the side menu and the page. */
  source: 'route' | 'menu';
}

export const PageGates: readonly PageGate[] = [
  { label: 'Learner Home', route: 'student-home', feature: 'Student', source: 'menu' },
  { label: 'Client Home', route: 'clientlanding', feature: 'ClientHomeView', source: 'menu' },
  { label: 'Tutor Home', route: 'tutorlanding', feature: 'TutorHomeView', source: 'menu' },
  { label: 'Tutor Co-ordinator Home', route: 'coordinatorlanding', feature: 'CoordinatorHomeView', source: 'menu' },
  { label: 'Sessions Home', route: 'tutorsessions', feature: 'StudentSessionsView', source: 'menu' },
  { label: 'Session Overview', route: 'overview', feature: 'SessionOverviewView', source: 'menu' },
  { label: 'Session Search', route: 'search', feature: 'SessionSearchView', source: 'menu' },
  { label: 'Missing Submissions', route: 'missing', feature: 'MissingSubmissionsView', source: 'menu' },
  { label: 'Pending Actions', route: 'actions', feature: 'ActionsView', source: 'menu' },
  { label: 'Coordinator tasks', route: 'tcdashboard', feature: 'TcDashboardView', source: 'menu' },
  { label: 'Learner Feedback dashboard', route: 'student-feedback-dashboard', feature: 'ViewStudentFeedbackDashboard', source: 'menu' },
  { label: 'Online Lesson Overview (client view)', route: 'online-lesson-overview', feature: 'OnlineOverviewClient', source: 'menu' },
  { label: 'Online Lesson Overview (admin view)', route: 'online-lesson-overview', feature: 'OnlineOverviewAdmin', source: 'menu' },
  { label: 'Bookings', route: 'booking-search', feature: 'BookingSearchView', source: 'menu' },
  { label: 'Reports', route: 'reports', feature: 'ReportsView', source: 'menu' },
  { label: 'QA Approval', route: 'qa-report-approval', feature: 'QaReportApprovalView', source: 'menu' },
  { label: 'Client Users', route: 'clientadmin', feature: 'ClientAdminEdit', source: 'menu' },
  { label: 'User Maintenance', route: 'user-maintenance', feature: 'UserMaintenanceView', source: 'route' },
  { label: 'User Maintenance: edit a user', route: 'user-maintenance/edit/:id', feature: 'UserMaintenanceEdit', source: 'route' },
  { label: 'Your Voice', route: 'feedback-survey', feature: 'Student', source: 'menu' },
  { label: 'Account settings', route: 'settings', feature: 'AccountSettingsEdit', source: 'menu' },
  { label: 'Email templates', route: 'email-templates', feature: 'EmailTemplatesView', switchKey: FeatureSwitchKeys.emailTemplatesCatalogue, source: 'route' },
  { label: 'Email history', route: 'email-history', feature: 'EmailHistoryView', switchKey: FeatureSwitchKeys.emailHistory, source: 'route' },
  { label: 'Live activity', route: 'live-activity', feature: 'LiveActivityView', switchKey: FeatureSwitchKeys.liveActivity, source: 'route' },
  { label: 'System Controls', route: 'service-processes', feature: 'ServiceProcessAdmin', role: 'ADMIN', source: 'route' },
  { label: 'System Controls: Settings', route: 'service-processes', feature: 'ServiceProcessAdmin', role: 'ADMIN', switchKey: FeatureSwitchKeys.settingsAdmin, source: 'route' },
  { label: 'System Controls: Nudger', route: 'service-processes', feature: 'ServiceProcessAdmin', role: 'ADMIN', switchKey: FeatureSwitchKeys.nudgerRulesEditor, source: 'route' },
  { label: 'New Features', route: 'feature-switches', feature: 'FeatureSwitchAdmin', role: 'ADMIN', source: 'route' },
  { label: 'Role Features', route: 'role-features', feature: 'RoleFeatureAdmin', role: 'ADMIN', source: 'route' },
];
